/**
 * 免許・資格名の入力サジェスト（Vue 非依存の純関数）。
 * データは licenseSuggestions.json（入力ランキング上位を正式名称へ寄せたもの）。
 */

type LicenseScore = {
  /** 入力中の文字列からスコアを拾う正規表現（NFKC 後の文字列に適用） */
  pattern: string
  /** `{n}` をスコアに置き換えて挿入する */
  template: string
}

type LicenseEntry = {
  name: string
  /** 挿入時に正式名称の後ろへ全角スペースを挟んで付ける語（取得 / 合格 / 資格取得）。修了系は無し */
  suffix?: string
  aliases?: string[]
  score?: LicenseScore
}

export type LicenseSuggestData = {
  keywords: { from: string; to: string }[]
  entries: LicenseEntry[]
}

type IndexedEntry = {
  entry: LicenseEntry
  name: string
  aliases: string[]
  text: string
}

export type LicenseSuggestIndex = {
  keywords: { from: string; to: string }[]
  items: IndexedEntry[]
}

const KANJI_DIGITS: Record<string, string> = {
  一: '1',
  二: '2',
  ニ: '2',
  三: '3',
  四: '4',
  五: '5'
}

/**
 * 比較用の正規化。全角半角・大小文字・区切り記号・ひらがな/カタカナ・
 * 「第二種」「二級」などの漢数字の揺れを吸収する。
 */
export function normalizeLicenseText(value: string): string {
  return value
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[ぁ-ゖ]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) + 0x60))
    .replace(
      /第?([一二ニ三四五])(?=[種級類])/g,
      (_m, d: string) => KANJI_DIGITS[d]
    )
    .replace(/第(?=\d[種級類])/g, '')
    .replace(/[\s・･\-‐–—_/()（）「」【】,，.．&]/g, '')
}

export function createLicenseSuggestIndex(
  data: LicenseSuggestData
): LicenseSuggestIndex {
  return {
    keywords: data.keywords.map(({ from, to }) => ({
      from: normalizeLicenseText(from),
      to: normalizeLicenseText(to)
    })),
    items: data.entries.map((entry) => ({
      entry,
      name: normalizeLicenseText(entry.name),
      aliases: (entry.aliases || []).map(normalizeLicenseText),
      text: entry.suffix ? entry.name + '　' + entry.suffix : entry.name
    }))
  }
}

function queryVariants(query: string, index: LicenseSuggestIndex): string[] {
  const variants = [query]
  index.keywords.forEach(({ from, to }) => {
    if (query.includes(from)) variants.push(query.replace(from, to))
  })
  return variants
}

function scoreText(entry: LicenseEntry, rawQuery: string): string {
  if (!entry.score) return ''
  const match = rawQuery
    .normalize('NFKC')
    .replace(/^.*?(toeic|ielts)/i, '')
    .match(new RegExp(entry.score.pattern))
  return match
    ? entry.score.template.replace('{n}', match[0])
    : `${entry.name} `
}

/**
 * 入力中の文字列に対する候補（挿入する文字列）を返す。
 * 前方一致 → 部分一致 → 別名一致の順、同順位は入力数の多い順。
 */
export function searchLicenseSuggestions(
  index: LicenseSuggestIndex,
  rawQuery: string,
  limit = 8
): string[] {
  const query = normalizeLicenseText(rawQuery).replace(/(取得|合格)$/, '')
  // 入力前はドロップダウンを出さない（おすすめはチップで出す）
  if (!query) return []

  const variants = queryVariants(query, index)
  const ranked: { rank: number; order: number; text: string }[] = []
  index.items.forEach((item, order) => {
    const targets = item.entry.score
      ? variants.map((v) => v.replace(/[\d.]+点?/g, '')).filter(Boolean)
      : variants
    const stripDigits = (s: string) =>
      item.entry.score ? s.replace(/[\d.]+点?/g, '') : s
    let rank = -1
    if (targets.some((v) => item.name.startsWith(v))) rank = 0
    else if (targets.some((v) => item.name.includes(v))) rank = 1
    else if (
      targets.some((v) => item.aliases.some((a) => stripDigits(a).includes(v)))
    ) {
      rank = 2
    }
    if (rank < 0) return
    ranked.push({
      rank,
      order,
      text: item.entry.score ? scoreText(item.entry, rawQuery) : item.text
    })
  })
  const texts = ranked
    .sort((a, b) => a.rank - b.rank || a.order - b.order)
    .slice(0, limit)
    .map((r) => r.text)
  // 候補どおりに入力し終えていれば出さない
  const typed = normalizeLicenseText(rawQuery)
  return texts.some((t) => normalizeLicenseText(t) === typed) ? [] : texts
}

/**
 * 入力前に並べるおすすめ（入力数の多い順）。入力済みの資格とスコアが要る TOEIC 等は除く。
 */
export function recommendLicenses(
  index: LicenseSuggestIndex,
  entered: string[],
  limit = 10
): { label: string; text: string }[] {
  const done = new Set(
    entered
      .map((v) =>
        normalizeLicenseText(v || '').replace(/(資格取得|取得|合格)$/, '')
      )
      .filter(Boolean)
  )
  // 「介護福祉士資格 取得」は入力側で末尾の「資格取得」まで落ちるので、名前末尾の「資格」を外しても照合する
  return index.items
    .filter(
      (item) =>
        !item.entry.score &&
        !done.has(item.name) &&
        !done.has(item.name.replace(/資格$/, ''))
    )
    .slice(0, limit)
    .map((item) => ({ label: item.entry.name, text: item.text }))
}

let indexPromise: Promise<LicenseSuggestIndex> | null = null

/** 候補データ（約 80KB）は初めて使うときにだけ読み込む */
export function loadLicenseSuggestIndex(): Promise<LicenseSuggestIndex> {
  if (!indexPromise) {
    indexPromise = import(
      /* webpackChunkName: "license-suggestions" */ './licenseSuggestions.json'
    )
      .then((mod) =>
        createLicenseSuggestIndex(
          ((mod as { default?: unknown }).default || mod) as LicenseSuggestData
        )
      )
      .catch((e) => {
        indexPromise = null
        throw e
      })
  }
  return indexPromise
}
